import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo} from "react";
import {pipelinesSelector, useGetAllPipelinesQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {PipelineDataTable} from "../../components/data-table/PipelineDataTable/PipelineDataTable";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";

export type PipelinePageProps = {}

export const PipelineDataTablePage = (props: PipelinePageProps) => {

  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp();
  const navigate = useNavigate()

  const selectPipelines = useMemo(() => {
    return pipelinesSelector();
  }, []);

  const {pipelines, isLoading, refetch,} = useGetAllPipelinesQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      pipelines: selectPipelines(result, undefined),
      isLoading: result.isLoading,
    })
  });

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.pipeline.title", "Pipelines", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              Actions
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["edit"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/pipelines/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <PipelineDataTable
                  withBorder
                  records={pipelines}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isLoading}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
